import React, { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import api from '../services/api'
import { Device } from '../types'
import { updateMetaTags } from '../utils/seo'

const MacBookServicePage: React.FC = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [activeTab, setActiveTab] = useState<'platinen' | 'reparatur'>(
    searchParams.get('tab') === 'reparatur' ? 'reparatur' : 'platinen'
  )

  const { data: modelsResponse, isLoading: modelsLoading } = useQuery({
    queryKey: ['devices', 'MacBook'],
    queryFn: () => api.getDevicesByBrand('MacBook'),
    enabled: true,
  })
  const macbookDevices: Device[] = modelsResponse?.data || []

  useEffect(() => {
    updateMetaTags({
      title: 'MacBook Reparatur – Platinen, Wasserschaden & Display | PhoneMedic',
      description: 'MacBook Platinen-Reparatur ✓ Wasserschaden ✓ MacBook startet nicht ✓ Display ab 229€ ✓ Akku ab 129€ ✓ Mikrochip-Löten ✓ 12 Monate Garantie ✓ Bundesweiter Versand | PhoneMedic',
      keywords: [
        'MacBook Reparatur',
        'MacBook Platinen Reparatur',
        'MacBook startet nicht',
        'MacBook Wasserschaden Reparatur',
        'Mikrochip Löten MacBook',
        'MacBook Logicboard Reparatur',
        'MacBook Display Reparatur',
        'MacBook Akku tauschen',
        'MacBook Pro Reparatur',
        'MacBook Air Reparatur',
      ],
      canonicalUrl: 'https://www.phone-medic.de/macbook-reparatur',
    })

    const structuredData = {
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: 'MacBook Reparatur & Platinen-Reparatur',
      description: 'Professionelle MacBook Reparatur: Platinen, Wasserschaden, Mikrochip-Löten, Display und Akku.',
      provider: {
        '@type': 'LocalBusiness',
        name: 'PhoneMedic',
        url: 'https://www.phone-medic.de',
        telephone: '0160-5010907',
        email: 'service@phone-medic.de',
      },
      serviceType: ['MacBook Reparatur', 'Platinen Reparatur', 'Mikrochip Löten'],
      areaServed: 'DE',
      offers: [
        { '@type': 'Offer', name: 'MacBook Platinen-Reparatur', price: '119.00', priceCurrency: 'EUR' },
        { '@type': 'Offer', name: 'MacBook Wasserschaden', price: '119.00', priceCurrency: 'EUR' },
        { '@type': 'Offer', name: 'MacBook Display Reparatur', price: '229.00', priceCurrency: 'EUR' },
        { '@type': 'Offer', name: 'MacBook Akku Austausch', price: '129.00', priceCurrency: 'EUR' },
      ],
    }

    let script = document.querySelector('#macbook-service-structured-data') as HTMLScriptElement
    if (!script) {
      script = document.createElement('script')
      script.id = 'macbook-service-structured-data'
      script.type = 'application/ld+json'
      document.head.appendChild(script)
    }
    script.textContent = JSON.stringify(structuredData)

    return () => { document.querySelector('#macbook-service-structured-data')?.remove() }
  }, [])

  const tabs = [
    { id: 'platinen' as const, label: 'Platinen-Reparatur', sub: 'Chip-Level · Wasserschaden · Kein Start' },
    { id: 'reparatur' as const, label: 'Standard-Reparatur', sub: 'Display, Akku & mehr' },
  ]

  const platinenServices = [
    {
      icon: '⚡',
      title: 'MacBook startet nicht',
      description: 'Kein Lebenszeichen? Oft ein Kurzschluss oder defekter Chip auf der Platine – kein Grund für einen Neukauf.',
      symptoms: ['Kein Lebenszeichen trotz Laden', 'Schaltet nach Sekunden wieder aus', 'MagSafe-LED leuchtet nicht'],
      price: 'ab 119 €',
    },
    {
      icon: '💧',
      title: 'Wasserschaden',
      description: 'Flüssigkeit hinterlässt Korrosion auf der Platine. Wir arbeiten unter dem Mikroskop und ersetzen beschädigte Bauteile.',
      symptoms: ['MacBook ins Wasser gefallen', 'Startet kurz, dann wieder aus', 'Teilweise Fehlfunktionen'],
      price: 'ab 119 €',
    },
    {
      icon: '💾',
      title: 'Datenrettung',
      description: 'Daten von defekten MacBook-Platinen retten – auch wenn das Gerät sich nicht mehr einschaltet.',
      symptoms: ['Kein Bild / kein Start nach Defekt', 'Daten nach Wasserschaden', 'Speicher nach Platinenschaden'],
      price: 'ab 119 €',
    },
  ]

  const standardServices = [
    { icon: '🖥️', title: 'Display Reparatur', description: 'Gesprungenes oder schwarzes Display? Wir tauschen alle MacBook Displays aus.', price: 'ab 229 €' },
    { icon: '🔋', title: 'Akku Austausch', description: 'MacBook lädt nicht oder Akku hält nicht? Professioneller Austausch für alle Modelle.', price: 'ab 129 €' },
    { icon: '⌨️', title: 'Topcase / Tastatur', description: 'Defekte oder klebende Tasten? Wir reparieren MacBook Tastaturen aller Generationen.', price: 'ab 129 €' },
    { icon: '🔌', title: 'Ladeport / USB-C', description: 'Laden funktioniert nicht? Wir tauschen defekte Ladeports und USB-C-Buchsen.', price: 'ab 79 €' },
  ]

  const faqs = [
    { q: 'Mein MacBook startet nicht – kann das repariert werden?', a: 'In den meisten Fällen ja. "MacBook startet nicht" ist oft ein Kurzschluss oder einzelner defekter Chip auf der Platine, nicht das ganze Logicboard. Wir diagnostizieren unter dem Mikroskop und reparieren gezielt.' },
    { q: 'Was kostet eine MacBook Platinen-Reparatur?', a: 'Platinen-Reparaturen beginnen ab 119 €. Die Diagnose kostet 24,95 € – danach erhältst du einen unverbindlichen Kostenvoranschlag und entscheidest, ob wir reparieren.' },
    { q: 'Mein MacBook hatte Wasserschaden – Apple lehnt die Reparatur ab.', a: 'Apple repariert keine Platinen auf Chip-Ebene. Wir schon. Erfolgsquote: 70–90 % je nach Schwere des Schadens.' },
    { q: 'Wie lange dauert eine MacBook Reparatur?', a: 'Standard-Reparaturen (Display, Akku) dauern 2–3 Werktage. Platinen-Reparaturen 3–5 Werktage. Express-Service auf Anfrage möglich.' },
    { q: 'Gehen bei der Reparatur meine Daten verloren?', a: 'Bei Standard-Reparaturen (Display, Akku, Tastatur) bleiben alle Daten erhalten. Bei Platinen-Reparaturen informieren wir dich vorher, falls der Speicher betroffen sein könnte.' },
    { q: 'Kann ich mein MacBook bundesweit einsenden?', a: 'Ja. Wir schicken dir ein kostenloses DHL-Versandlabel. Nach der Reparatur kommt dein MacBook versichert zurück.' },
  ]

  return (
    <div className="min-h-screen bg-white">
      {/* Hero */}
      <section
        className="relative text-white py-20"
        style={{ backgroundImage: 'url(/hero-bg.webp)', backgroundSize: 'cover', backgroundPosition: 'center top' }}
      >
        <div className="absolute inset-0 bg-gray-900/80" />
        <div className="relative max-w-7xl mx-auto px-4">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-indigo-600/20 border border-indigo-500/30 text-indigo-300 text-sm font-medium px-3 py-1 rounded-full mb-6">
              <span className="w-1.5 h-1.5 bg-indigo-400 rounded-full"></span>
              MacBook Spezialist · Mikrochip-Löten
            </div>
            <h1 className="text-5xl font-bold mb-6 leading-tight">
              MacBook Reparatur –<br />
              <span className="text-indigo-400">Platinen, Wasserschaden & Display</span>
            </h1>
            <p className="text-xl text-gray-300 mb-8 max-w-2xl">
              Von Display-Austausch bis Chip-Level-Löten. Wir reparieren MacBooks, bei denen andere Werkstätten aufgeben. Bundesweiter Einsende-Service.
            </p>
            <div className="flex flex-wrap gap-4">
              <a
                href="mailto:service@phone-medic.de"
                className="bg-indigo-600 text-white px-7 py-3.5 rounded-lg font-semibold hover:bg-indigo-700 transition-colors"
              >
                Kostenvoranschlag anfragen
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="bg-gray-800 text-white py-5 border-t border-gray-700">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
            {[
              { number: 'ab 119 €', label: 'Platinen-Reparatur' },
              { number: 'ab 229 €', label: 'Display Reparatur' },
              { number: '70–90 %', label: 'Erfolgsquote Platinen' },
              { number: '12 Monate', label: 'Garantie' },
            ].map((s, i) => (
              <div key={i}>
                <div className="text-2xl font-bold text-indigo-400">{s.number}</div>
                <div className="text-sm text-gray-400">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Tabs */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-gray-900 mb-3">Unsere MacBook Services</h2>
            <p className="text-gray-500">Wähle den passenden Service</p>
          </div>

          {/* Tab Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 justify-center mb-10">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-6 py-4 rounded-xl font-semibold text-left sm:text-center transition-all border-2 ${
                  activeTab === tab.id
                    ? 'bg-white border-indigo-600 text-indigo-700 shadow-md'
                    : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'
                }`}
              >
                <div className="text-base">{tab.label}</div>
                <div className="text-xs font-normal text-gray-400 mt-0.5">{tab.sub}</div>
              </button>
            ))}
          </div>

          {/* Tab Content */}
          {activeTab === 'platinen' && (
            <div>
              <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-4 mb-8 text-center">
                <p className="text-indigo-800 font-medium text-sm">
                  Professionelle Platinen-Reparatur auf Chip-Ebene – mit Mikroskop und Mikrolöt-Station für präzise Diagnose und Reparatur
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {platinenServices.map((s, i) => (
                  <div key={i} className="bg-white rounded-xl border border-gray-200 p-6 flex flex-col hover:shadow-md transition-shadow">
                    <h3 className="text-lg font-bold text-gray-900 mb-2">{s.title}</h3>
                    <p className="text-sm text-gray-500 mb-4 flex-1">{s.description}</p>
                    <div className="mb-4">
                      <p className="text-xs font-semibold text-gray-600 mb-1.5">Typische Symptome:</p>
                      <ul className="space-y-1">
                        {s.symptoms.map((sym, j) => (
                          <li key={j} className="text-xs text-gray-500 flex items-start gap-1.5">
                            <span className="text-indigo-600 mt-0.5 shrink-0">✓</span>{sym}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div className="border-t pt-4 flex justify-between items-center">
                      <span className="text-xl font-bold text-indigo-600">{s.price}</span>
                      <a href="mailto:service@phone-medic.de" className="bg-indigo-600 text-white px-4 py-1.5 rounded-lg text-sm font-medium hover:bg-indigo-700 transition-colors">
                        Anfragen
                      </a>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-8 bg-white rounded-xl border border-gray-200 p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-bold text-gray-700 mb-3">Normale Werkstatt</h4>
                    <ul className="space-y-2 text-sm text-gray-600">
                      <li className="flex gap-2"><span className="text-indigo-500 shrink-0">✗</span> Tauscht nur ganze Baugruppen aus</li>
                      <li className="flex gap-2"><span className="text-indigo-500 shrink-0">✗</span> Kann keine einzelnen Chips ersetzen</li>
                      <li className="flex gap-2"><span className="text-indigo-500 shrink-0">✗</span> Gibt bei Wasserschaden auf</li>
                      <li className="flex gap-2"><span className="text-indigo-500 shrink-0">✗</span> Empfiehlt Neukauf für 1.000 €+</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-3">PhoneMedic</h4>
                    <ul className="space-y-2 text-sm text-gray-600">
                      <li className="flex gap-2"><span className="text-green-600 shrink-0">✓</span> Diagnose unter dem Mikroskop</li>
                      <li className="flex gap-2"><span className="text-green-600 shrink-0">✓</span> Defekte SMD-Chips direkt ersetzen</li>
                      <li className="flex gap-2"><span className="text-green-600 shrink-0">✓</span> Korrosion professionell entfernen</li>
                      <li className="flex gap-2"><span className="text-green-600 shrink-0">✓</span> Oft ab 119 € statt Neukauf</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Model picker for platinen tab */}
              <div className="mt-8">
                <h3 className="text-xl font-bold text-gray-900 mb-2">MacBook Modell wählen & Diagnose buchen</h3>
                <p className="text-sm text-gray-500 mb-6">Wähle dein Modell um alle verfügbaren Reparaturen und genaue Preise zu sehen.</p>
                {modelsLoading ? (
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                    {Array.from({ length: 8 }).map((_, i) => (
                      <div key={i} className="h-16 bg-gray-100 rounded-lg animate-pulse" />
                    ))}
                  </div>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                    {macbookDevices.map((device) => (
                      <button
                        key={device._id}
                        onClick={() => navigate(`/${device.brandSlug}/${device.slug}`)}
                        className="bg-white border border-gray-200 rounded-lg px-4 py-3 text-sm font-medium text-gray-700 text-left hover:border-indigo-400 hover:text-indigo-700 hover:bg-indigo-50 transition-colors"
                      >
                        {device.deviceModel}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'reparatur' && (
            <div>
              {/* Price overview */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
                {standardServices.map((s, i) => (
                  <div key={i} className="bg-white rounded-xl border border-gray-200 p-6 text-center hover:shadow-md transition-shadow flex flex-col">
                    <h3 className="text-base font-bold text-gray-900 mb-2">{s.title}</h3>
                    <p className="text-sm text-gray-500 flex-1 mb-4">{s.description}</p>
                    <div className="text-xl font-bold text-indigo-600">{s.price}</div>
                  </div>
                ))}
              </div>

              {/* Model picker */}
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">MacBook Modell wählen & direkt buchen</h3>
                <p className="text-sm text-gray-500 mb-6">Wähle dein Modell um alle verfügbaren Reparaturen und genaue Preise zu sehen.</p>
                {modelsLoading ? (
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                    {Array.from({ length: 8 }).map((_, i) => (
                      <div key={i} className="h-16 bg-gray-100 rounded-lg animate-pulse" />
                    ))}
                  </div>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                    {macbookDevices.map((device) => (
                      <button
                        key={device._id}
                        onClick={() => navigate(`/${device.brandSlug}/${device.slug}`)}
                        className="bg-white border border-gray-200 rounded-lg px-4 py-3 text-sm font-medium text-gray-700 text-left hover:border-indigo-400 hover:text-indigo-700 hover:bg-indigo-50 transition-colors"
                      >
                        {device.deviceModel}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Process */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-3">So funktioniert's</h2>
            <p className="text-gray-500">Transparent, fair, ohne Überraschungen</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              { step: '1', title: 'Kontakt aufnehmen', desc: 'E-Mail oder WhatsApp – wir schicken dir ein kostenloses DHL-Versandlabel.' },
              { step: '2', title: 'MacBook einsenden', desc: 'Sicher verpacken und einsenden. Wir bestätigen den Eingang.' },
              { step: '3', title: 'Diagnose & Angebot', desc: 'Wir analysieren dein MacBook und senden dir ein verbindliches Angebot.' },
              { step: '4', title: 'Reparatur & Rücksendung', desc: 'Nach deiner Freigabe reparieren wir es und schicken es versichert zurück.' },
            ].map((item, i) => (
              <div key={i} className="text-center flex flex-col items-center">
                <div className="w-12 h-12 bg-gray-900 text-white rounded-full flex items-center justify-center text-lg font-bold mb-4">
                  {item.step}
                </div>
                <h3 className="font-bold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-sm text-gray-500">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Subpage Links */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-gray-900 mb-3">Spezifische MacBook Probleme</h2>
            <p className="text-gray-500">Detaillierte Infos, Preise und FAQs für jedes Problem</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              { title: 'MacBook startet nicht', desc: 'Kein Lebenszeichen, schwarzer Bildschirm, schaltet sofort wieder aus. Ursachen und Chip-Level Reparatur ab 119 €.', path: '/macbook-startet-nicht', tag: 'Platinen-Reparatur' },
              { title: 'MacBook Wasserschaden', desc: 'Sofortmaßnahmen bei Flüssigkeitsschäden, Platinen-Reinigung und Reparatur. Erfolgsquote 70–90 %.', path: '/macbook-wasserschaden', tag: 'Notfall' },
              { title: 'MacBook Akku tauschen', desc: 'Kurze Laufzeit, aufgeblähter Akku oder Meldung "Akku warten"? Preise für alle Modelle.', path: '/macbook-akku-tauschen', tag: 'ab 129 €' },
              { title: 'MacBook Logicboard Reparatur', desc: 'Chip-Level Mikrochip-Löten statt 800–1.500 € Logicboard-Tausch bei Apple.', path: '/macbook-logicboard-reparatur', tag: 'ab 119 €' },
              { title: 'MacBook Display Reparatur', desc: 'Gesprungenes oder schwarzes Display tauschen. Retina & Liquid Retina XDR alle Modelle.', path: '/macbook-display-reparatur', tag: 'ab 229 €' },
              { title: 'MacBook Reparatur Regensburg', desc: 'PhoneMedic in Regensburg – bundesweiter Einsende-Service für alle MacBook Modelle.', path: '/macbook-reparatur-regensburg', tag: 'Regensburg' },
            ].map((item) => (
              <button
                key={item.path}
                onClick={() => navigate(item.path)}
                className="bg-gray-50 border border-gray-200 rounded-xl p-6 text-left hover:border-indigo-300 hover:bg-indigo-50 transition-colors group"
              >
                <div className="flex items-start justify-between mb-2">
                  <h3 className="font-bold text-gray-900 group-hover:text-indigo-700 transition-colors">{item.title}</h3>
                  <span className="text-xs bg-gray-200 group-hover:bg-indigo-200 text-gray-600 group-hover:text-indigo-700 px-2 py-0.5 rounded-full shrink-0 ml-2 transition-colors">{item.tag}</span>
                </div>
                <p className="text-sm text-gray-500 leading-relaxed">{item.desc}</p>
                <span className="text-sm text-indigo-600 font-medium mt-3 inline-block">Mehr erfahren →</span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-3xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-10">Häufige Fragen</h2>
          <div className="space-y-4">
            {faqs.map((faq, i) => (
              <div key={i} className="bg-gray-50 border border-gray-200 rounded-xl p-6">
                <h3 className="font-semibold text-gray-900 mb-2">{faq.q}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-gray-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-3">MacBook kaputt? Wir schauen es uns an.</h2>
          <p className="text-gray-400 mb-8">Diagnosekosten ab 24,95 € · Kostenvoranschlag vor Reparaturbeginn</p>
          <div className="flex flex-wrap justify-center gap-4">
            <a
              href="mailto:service@phone-medic.de"
              className="bg-indigo-600 text-white px-7 py-3.5 rounded-lg font-semibold hover:bg-indigo-700 transition-colors"
            >
              Per E-Mail anfragen
            </a>
          </div>
        </div>
      </section>
    </div>
  )
}

export default MacBookServicePage
